import React, { useState, useEffect } from 'react';
import { API_BASE, apiHeaders, apiPut, leseFallback, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { MIcon } from '../../lib/micon';
import { LdFeld, LdEingabe, LdUhrzeit, LdChips } from '../../components/ld/LdFenster';
import { EinstAbschnitt, EinstSchalter, EinstSpeichern } from './einstellungen-rahmen';

type Fenster = [string, string];
export interface Sprechzeit {
  wochentag_fenster: Record<string, Fenster[]>;
  termin_dauer_min: number;
  puffer_min: number;
  max_termine_pro_woche: number;
  default_typ: 'vor_ort' | 'telefonisch';
}

const WOCHENTAGE = [
  { nr: 1, name: 'Montag', kurz: 'Mo' }, { nr: 2, name: 'Dienstag', kurz: 'Di' }, { nr: 3, name: 'Mittwoch', kurz: 'Mi' },
  { nr: 4, name: 'Donnerstag', kurz: 'Do' }, { nr: 5, name: 'Freitag', kurz: 'Fr' },
];

/** Kurzfassung für die Übersicht: „Mo bis Fr, 14:30 bis 17:30“ / „Mo, Mi, 13:00 bis 14:00 …“ */
export function sprechzeitKurz(w: Record<string, Fenster[]> | null | undefined): string {
  if (!w) return '';
  const tage = WOCHENTAGE.filter(t => w[String(t.nr)]?.length);
  if (tage.length === 0) return 'keine Sprechzeit';
  const nrs = tage.map(t => t.nr);
  const lueckenlos = nrs.every((n, i) => i === 0 || n === nrs[i - 1] + 1);
  const tageText = tage.length >= 3 && lueckenlos ? `${tage[0].kurz} bis ${tage[tage.length - 1].kurz}` : tage.map(t => t.kurz).join(', ');
  const fenster = tage.map(t => JSON.stringify(w[String(t.nr)]));
  const gleich = fenster.every(f => f === fenster[0]) && w[String(tage[0].nr)].length === 1;
  return gleich ? `${tageText}, ${w[String(tage[0].nr)][0][0]} bis ${w[String(tage[0].nr)][0][1]}` : tageText;
}

export function ElternSprechzeitSettings({ onGeaendert }: { onGeaendert?: (s: Sprechzeit) => void } = {}) {
  const [s, setS]         = useState<Sprechzeit | null>(null);
  const [speichern, setSpeichern] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/lehrer/sprechzeit`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/lehrer/sprechzeit'))
      .then(d => { if (d?.settings) setS({
        wochentag_fenster: d.settings.wochentag_fenster ?? {},
        termin_dauer_min: d.settings.termin_dauer_min ?? 30,
        puffer_min: d.settings.puffer_min ?? 15,
        max_termine_pro_woche: d.settings.max_termine_pro_woche ?? 4,
        default_typ: d.settings.default_typ ?? 'vor_ort',
      }); })
      .catch(() => {});
  }, []);

  if (!s) return <p className="ld-fe-text">Lädt …</p>;

  function setFenster(fn: (f: Record<string, Fenster[]>) => Record<string, Fenster[]>) {
    setS(prev => prev ? { ...prev, wochentag_fenster: fn({ ...prev.wochentag_fenster }) } : prev);
  }
  function toggleTag(nr: number) {
    const key = String(nr);
    setFenster(f => {
      if (f[key]) { delete f[key]; } else { f[key] = [['14:30', '17:30']]; }
      return f;
    });
  }
  function addFenster(nr: number) {
    const key = String(nr);
    setFenster(f => { f[key] = [...(f[key] ?? []), ['14:00', '15:00']]; return f; });
  }
  function removeFenster(nr: number, i: number) {
    const key = String(nr);
    setFenster(f => {
      const arr = (f[key] ?? []).filter((_, j) => j !== i);
      if (arr.length === 0) delete f[key]; else f[key] = arr;
      return f;
    });
  }
  function updateZeit(nr: number, i: number, idx: 0 | 1, val: string) {
    const key = String(nr);
    setFenster(f => {
      const arr = (f[key] ?? []).map((t, j) => j === i ? (idx === 0 ? [val, t[1]] : [t[0], val]) as Fenster : t);
      f[key] = arr; return f;
    });
  }

  async function speichernKlick() {
    // Validierung
    for (const [k, arr] of Object.entries(s!.wochentag_fenster)) {
      for (const [start, end] of arr) {
        if (!start || !end) { toast(`Bitte alle Zeitfenster ausfüllen (${WOCHENTAGE.find(w => String(w.nr) === k)?.name}).`, 'error'); return; }
        if (end <= start) { toast(`Ende muss nach Start liegen (${WOCHENTAGE.find(w => String(w.nr) === k)?.name}).`, 'error'); return; }
      }
    }
    if (s!.termin_dauer_min < 15) { toast('Termin-Dauer mindestens 15 Min.', 'error'); return; }
    if (s!.max_termine_pro_woche < 1) { toast('Höchstens-Wert mindestens 1.', 'error'); return; }
    if (Object.keys(s!.wochentag_fenster).length === 0) toast('Hinweis: Ohne aktiven Wochentag können keine Termine vorgeschlagen werden.', 'info');

    setSpeichern(true);
    try {
      await apiPut('/api/lehrer/sprechzeit', s);
      toast('Sprechzeit gespeichert', 'success');
      onGeaendert?.(s!);
    } catch (e) { zeigeApiFehler(e, 'Speichern fehlgeschlagen'); }
    finally { setSpeichern(false); }
  }

  const zahl = (key: 'termin_dauer_min' | 'puffer_min' | 'max_termine_pro_woche', label: string, einheit: string, min: number, max: number) => (
    <LdFeld label={label}>
      <LdEingabe type="number" inputMode="numeric" min={min} max={max} aria-label={label} value={s[key]}
        onChange={e => setS({ ...s, [key]: Number(e.target.value) })}
        rechts={<span className="ld-es-einheit">{einheit}</span>} className="ld-es-zahl" />
    </LdFeld>
  );

  return (
    <>
      <EinstAbschnitt titel="Tage und Zeiten">
        <div className="ld-es-tage">
          {WOCHENTAGE.map(wt => {
            const key = String(wt.nr);
            const aktiv = !!s.wochentag_fenster[key];
            return (
              <div key={wt.nr} className={`ld-es-tag${aktiv ? '' : ' ist-aus'}`}>
                <EinstSchalter an={aktiv} onChange={() => toggleTag(wt.nr)} label={`${wt.name} Sprechzeit`} />
                <b>{wt.name}</b>
                <div className="ld-es-tag__fenster">
                  {!aktiv ? <span className="ld-es-tag__leer">keine Sprechzeit</span> : (
                    <>
                      {(s.wochentag_fenster[key] ?? []).map((f, i) => (
                        <span key={i} className="ld-es-fenster">
                          <LdUhrzeit kompakt aria-label={`${wt.name} von`} placeholder="von" value={f[0]} onChange={v => updateZeit(wt.nr, i, 0, v)} />
                          <span className="ld-es-bis">bis</span>
                          <LdUhrzeit kompakt aria-label={`${wt.name} bis`} placeholder="bis" value={f[1]} onChange={v => updateZeit(wt.nr, i, 1, v)} />
                          {(s.wochentag_fenster[key] ?? []).length > 1 && (
                            <button type="button" className="ld-es-fenster__weg" aria-label={`Zeitfenster ${i + 1} am ${wt.name} entfernen`} onClick={() => removeFenster(wt.nr, i)}>
                              <MIcon name="close" size={16} />
                            </button>
                          )}
                        </span>
                      ))}
                      <button type="button" className="ld-kl-link" onClick={() => addFenster(wt.nr)}><MIcon name="add" size={17} />Zeitfenster</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </EinstAbschnitt>

      <EinstAbschnitt titel="Gespräche">
        <div className="ld-es-drei">
          {zahl('termin_dauer_min', 'Dauer', 'Minuten', 15, 90)}
          {zahl('puffer_min', 'Puffer danach', 'Minuten', 0, 60)}
          {zahl('max_termine_pro_woche', 'Höchstens', 'pro Woche', 1, 20)}
        </div>
      </EinstAbschnitt>

      <EinstAbschnitt titel="Art">
        <LdChips aria-label="Art" wert={s.default_typ} onChange={v => setS({ ...s, default_typ: v })}
          optionen={[{ wert: 'vor_ort' as const, label: 'Vor Ort', icon: 'school' }, { wert: 'telefonisch' as const, label: 'Telefonisch', icon: 'call' }]} />
      </EinstAbschnitt>

      <EinstSpeichern onClick={speichernKlick} laden={speichern} hinweis="Gilt für neue Terminvorschläge. Bestätigte Termine bleiben." />
    </>
  );
}
